@if (syncService.hasAnyVisibleJobs()) {
  <div class="sync-widget">

    <!-- ── Expanded Card (sits above the pill) ── -->
    @if (isExpanded()) {
      <div class="sync-card">

        <!-- Header -->
        <div class="card-header">
          <div class="header-left">
            <div class="header-icon" [class.spinning]="syncService.hasActiveJobs()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M21 12a9 9 0 0 0-9-9M3 12a9 9 0 0 0 9 9" stroke-linecap="round"/>
                <path d="M21 3v9h-9M3 21v-9h9" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="card-title">Transaction Sync</h3>
          </div>
          @if (syncService.hasActiveJobs()) {
            <span class="live-badge">
              <span class="live-dot"></span>
              Live
            </span>
          } @else {
            <span class="done-badge">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="12 5 7 11 4 8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Done
            </span>
          }
        </div>

        <!-- Body -->
        <div class="card-body">

          <!-- ── Active Jobs ── -->
          @for (job of syncService.activeJobs(); track job.job_id) {
            <div class="job-item active">
              <div class="job-row">

                <!-- Avatar -->
                <div class="source-avatar">
                  {{ getInitial(job.source_name) }}
                </div>

                <!-- Info -->
                <div class="job-info">
                  <div class="job-meta">
                    <span class="job-source">{{ formatSourceName(job.source_name) }}</span>
                    <span class="job-badge syncing">
                      <span class="badge-dot"></span>
                      {{ job.status === 'pending' ? 'Starting' : 'Syncing' }}
                    </span>
                  </div>

                  <!-- Scanner progress bar -->
                  <div class="progress-track">
                    <div class="progress-beam"></div>
                  </div>

                  <!-- Current step -->
                  @if (job.current_step) {
                    <p class="job-step">{{ job.current_step }}</p>
                  }

                  <!-- Stats row -->
                  <div class="job-stats">
                    @if (job.synced_count > 0) {
                      <span class="stat accent">{{ formatCount(job.synced_count) }} synced</span>
                    }
                    @if (job.skipped_count > 0) {
                      <span class="stat-sep">·</span>
                      <span class="stat muted">{{ job.skipped_count }} skipped</span>
                    }
                    @if (job.started_at) {
                      <span class="stat-sep">·</span>
                      <span class="stat muted">{{ getElapsed(job.started_at) }}</span>
                    }
                  </div>
                </div>

              </div>
            </div>
          }

          <!-- ── Completed / Failed Jobs ── -->
          @for (job of syncService.recentJobs(); track job.job_id) {
            <div class="job-item"
                 [class.completed]="job.status === 'completed'"
                 [class.failed]="job.status === 'failed'">
              <div class="job-row">

                <!-- Status avatar -->
                <div class="source-avatar"
                     [class.avatar-done]="job.status === 'completed'"
                     [class.avatar-fail]="job.status === 'failed'">
                  @if (job.status === 'completed') {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="check-icon">
                      <polyline points="20 6 9 17 4 12" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  } @else {
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="x-icon">
                      <line x1="18" y1="6" x2="6" y2="18" stroke-linecap="round"/>
                      <line x1="6" y1="6" x2="18" y2="18" stroke-linecap="round"/>
                    </svg>
                  }
                </div>

                <!-- Info -->
                <div class="job-info">
                  <div class="job-meta">
                    <span class="job-source">{{ formatSourceName(job.source_name) }}</span>
                    <span class="job-badge"
                          [class.badge-complete]="job.status === 'completed'"
                          [class.badge-failed]="job.status === 'failed'">
                      {{ job.status === 'completed' ? 'Complete' : 'Failed' }}
                    </span>
                  </div>

                  @if (job.status === 'completed') {
                    <p class="job-step success-text">
                      {{ formatCount(job.synced_count) }} transactions synced
                      @if (job.skipped_count > 0) {
                        &nbsp;·&nbsp;{{ job.skipped_count }} skipped
                      }
                    </p>
                  } @else if (job.error_message) {
                    <p class="job-step error-text">{{ job.error_message }}</p>
                  }
                </div>

              </div>
            </div>
          }

          @if (syncService.activeJobs().length === 0 && syncService.recentJobs().length === 0) {
            <p class="empty-state">No active syncs</p>
          }
        </div>
      </div>
    }

    <!-- ── Pill ── -->
    <button class="sync-pill"
            (click)="toggleExpanded()"
            [class.is-active]="syncService.hasActiveJobs()"
            [class.is-expanded]="isExpanded()">

      <!-- Breathing ring (active only) -->
      @if (syncService.hasActiveJobs()) {
        <span class="pill-ring" aria-hidden="true"></span>
      }

      <span class="pill-inner">
        <!-- Sync / check icon -->
        <span class="pill-icon-wrap" [class.spinning]="syncService.hasActiveJobs()">
          @if (syncService.hasActiveJobs()) {
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M21 12a9 9 0 0 0-9-9M3 12a9 9 0 0 0 9 9" stroke-linecap="round"/>
              <path d="M21 3v9h-9M3 21v-9h9" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          } @else {
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="check-anim">
              <polyline points="20 6 9 17 4 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
        </span>

        <!-- Label -->
        <span class="pill-label">
          @if (syncService.hasActiveJobs()) {
            @if (syncService.activeJobs().length === 1) {
              Syncing {{ formatSourceName(syncService.activeJobs()[0].source_name) }}
            } @else {
              Syncing {{ syncService.activeJobs().length }} sources
            }
          } @else {
            Sync complete
          }
        </span>

        <!-- Chevron -->
        <svg class="pill-chevron" [class.flipped]="isExpanded()"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="18 15 12 9 6 15" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </button>

  </div>
}
